import React, { createContext, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { THEME_STORAGE } from '@/constants/storage';
import { getLoggerWithPrefix } from '@/services';

export type Theme = 'light' | 'dark' | 'system';

interface ThemeContextType {
  theme: Theme;
  actualTheme: 'light' | 'dark';
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
  defaultTheme?: Theme;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({
  children,
  defaultTheme = 'system',
}) => {
  const logger = getLoggerWithPrefix('ThemeProvider');
  // Get system preference
  const getSystemTheme = (): 'light' | 'dark' => {
    if (typeof window === 'undefined' || !window.matchMedia) return 'light';
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  };

  // Calculate actual theme based on theme setting
  const calculateActualTheme = (themeValue: Theme): 'light' | 'dark' => {
    if (themeValue === 'system') {
      return getSystemTheme();
    }
    return themeValue;
  };

  const [theme, setThemeState] = useState<Theme>(() => {
    // Initialize theme from localStorage on first render
    try {
      const storedTheme = localStorage.getItem(THEME_STORAGE.PREFERENCE) as Theme;
      if (storedTheme && ['light', 'dark', 'system'].includes(storedTheme)) {
        return storedTheme;
      }
    } catch (error) {
      logger.warn('Failed to load theme from localStorage:', error);
    }
    return defaultTheme;
  });

  const [actualTheme, setActualTheme] = useState<'light' | 'dark'>(() => {
    // Initialize actual theme based on the initial theme
    try {
      const storedTheme = localStorage.getItem(THEME_STORAGE.PREFERENCE) as Theme;
      if (storedTheme && ['light', 'dark', 'system'].includes(storedTheme)) {
        return calculateActualTheme(storedTheme);
      }
    } catch (error) {
      logger.warn('Failed to load theme from localStorage:', error);
    }
    return calculateActualTheme(defaultTheme);
  });

  // Update actual theme when theme changes (for system theme updates)
  useEffect(() => {
    setActualTheme(calculateActualTheme(theme));
  }, [theme]);

  // Listen for system theme changes
  useEffect(() => {
    if (theme !== 'system' || typeof window === 'undefined' || !window.matchMedia) return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      setActualTheme(getSystemTheme());
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [theme]);

  // Apply theme to document
  useEffect(() => {
    const root = document.documentElement;

    // Remove existing theme classes
    root.classList.remove('light', 'dark');

    // Add current theme class
    root.classList.add(actualTheme);

    // Set color-scheme for better browser integration
    root.style.colorScheme = actualTheme;
  }, [actualTheme]);

  const setTheme = (newTheme: Theme) => {
    // Validate theme value before saving
    if (!['light', 'dark', 'system'].includes(newTheme)) {
      logger.warn('Invalid theme value:', newTheme);
      return;
    }

    try {
      localStorage.setItem(THEME_STORAGE.PREFERENCE, newTheme);
    } catch (error) {
      logger.warn('Failed to save theme to localStorage:', error);
    }

    setThemeState(newTheme);
    setActualTheme(calculateActualTheme(newTheme));
  };

  const toggleTheme = () => {
    if (theme === 'system') {
      // If system, switch to opposite of current actual theme
      setTheme(actualTheme === 'dark' ? 'light' : 'dark');
    } else {
      // If explicit theme, toggle between light and dark
      setTheme(theme === 'dark' ? 'light' : 'dark');
    }
  };

  const value: ThemeContextType = {
    theme,
    actualTheme,
    setTheme,
    toggleTheme,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useThemeContext = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useThemeContext must be used within a ThemeProvider');
  }
  return context;
};

export default ThemeProvider;